import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import { FaArrowLeft, FaArrowRight, FaCalendarAlt, FaClock, FaPlus, FaMinus, FaTrash } from 'react-icons/fa';
import Calendar from 'react-calendar';
import { setSelectedDate, updateItemQuantity, addOrderItem, removeOrderItem } from '../../redux/slices/tiffinSlice';
import { getColors } from '../../styles/colors';
import 'react-calendar/dist/Calendar.css';

const DateSelect = () => {
  const dispatch = useDispatch();
  const { orderItems, selectedDate } = useSelector((state) => state.tiffin);
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedMealForDate, setSelectedMealForDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedMealType, setSelectedMealType] = useState('Lunch');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('12:00 PM');

  const hasItems = orderItems.length > 0;

  // Time slots
  const timeSlots = [];
  for (let i = 8; i <= 22; i++) {
    const hour = i > 12 ? i - 12 : i;
    const ampm = i >= 12 ? 'PM' : 'AM';
    timeSlots.push(`${hour}:00 ${ampm}`);
    timeSlots.push(`${hour}:30 ${ampm}`);
  }

  const mealTypes = ['Breakfast', 'Lunch', 'Dinner'];

  const handleDateSelect = (date) => {
    const formattedDate = date.toISOString().split('T')[0];
    dispatch(setSelectedDate(formattedDate));
    setShowCalendar(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Click to select date';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const handleAddDateForMeal = (mealId, date, mealType, time) => {
    const meal = orderItems.find(item => item.id === mealId);
    if (meal) {
      dispatch(addOrderItem({ 
        item: { 
          ...meal, 
          scheduledDate: date, 
          scheduledTime: time,
          mealTypeSelected: mealType 
        }, 
        mealType: meal.category 
      }));
    }
    setShowDatePicker(false);
    setSelectedMealForDate(null);
  };

  // Group items by date
  const groupedByDate = orderItems.reduce((acc, item) => {
    const date = item.scheduledDate || 'Unassigned';
    if (!acc[date]) acc[date] = [];
    acc[date].push(item);
    return acc;
  }, {});

  if (!hasItems) {
    return (
      <motion.div 
        className="py-4 px-4 max-w-4xl mx-auto"
        style={{ backgroundColor: colors.background }}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center gap-4 mb-6">
          <Link to="/meal-select" className="text-2xl" style={{ color: colors.text }}>
            <FaArrowLeft />
          </Link>
          <h1 className="text-2xl font-bold" style={{ color: colors.text }}>📅 Select Date</h1>
        </div>
        <div 
          className="rounded-2xl p-12 text-center"
          style={{ 
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`
          }}
        >
          <p className="text-4xl mb-4">🛒</p>
          <p className="text-lg" style={{ color: colors.textSecondary }}>No items in your order</p>
          <Link to="/meal-select">
            <button 
              className="mt-4 px-6 py-3 rounded-full font-semibold hover:scale-105 transition-transform"
              style={{ backgroundColor: colors.primary, color: '#FFFFFF' }}
            >
              Browse Meals
            </button>
          </Link>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="py-4 px-4 max-w-4xl mx-auto"
      style={{ backgroundColor: colors.background }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="flex items-center gap-4 mb-6">
        <Link to="/meal-select" className="text-2xl" style={{ color: colors.text }}>
          <FaArrowLeft />
        </Link>
        <h1 className="text-2xl font-bold" style={{ color: colors.text }}>📅 Select Date & Time</h1>
      </div>

      {/* Calendar for selecting date */}
      <div 
        className="rounded-2xl p-6 mb-6"
        style={{ 
          backgroundColor: colors.card,
          border: `1px solid ${colors.border}`
        }}
      >
        <h2 className="text-lg font-bold mb-4" style={{ color: colors.text }}>
          Select Your Booking Date
        </h2>
        <div 
          className="flex items-center justify-between p-4 rounded-xl cursor-pointer hover:shadow-md transition-all"
          style={{ 
            backgroundColor: colors.inputBackground,
            border: `1px solid ${colors.border}`
          }}
          onClick={() => setShowCalendar(!showCalendar)}
        >
          <span style={{ color: colors.text }}>{formatDate(selectedDate)}</span>
          <FaCalendarAlt style={{ color: colors.primary }} />
        </div>
        {showCalendar && (
          <div className="mt-4">
            <Calendar 
              onChange={handleDateSelect} 
              value={selectedDate ? new Date(selectedDate) : new Date()}
              minDate={new Date()}
              className="w-full"
            />
          </div>
        )}
        {selectedDate && (
          <div className="mt-4 p-3 rounded-xl text-sm" style={{ backgroundColor: colors.primary + '20', color: colors.primary }}>
            ⚠️ Today's ordering window has closed. This will be ordered as tomorrow's lunch.
          </div>
        )}
      </div>

      {/* Meal Schedule - Multiple Dates */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold" style={{ color: colors.text }}>
          📋 Meal Schedule
        </h2>

        {/* Display meals grouped by date */}
        {Object.entries(groupedByDate).map(([date, items]) => (
          <div
            key={date}
            className="rounded-2xl p-4"
            style={{ 
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold" style={{ color: colors.primary }}>
                📅 {date === 'Unassigned' ? 'Unassigned' : formatDate(date)}
              </h3>
              <span className="text-sm" style={{ color: colors.textSecondary }}>
                {items.length} items
              </span>
            </div>
            
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 rounded-xl mb-2"
                style={{ 
                  backgroundColor: colors.inputBackground,
                  border: `1px solid ${colors.border}`
                }}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold" style={{ color: colors.text }}>{item.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ 
                      backgroundColor: item.isVeg ? '#2E7D3220' : '#C6282820',
                      color: item.isVeg ? '#2E7D32' : '#C62828'
                    }}>
                      {item.isVeg ? 'Veg' : 'Non-Veg'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-sm font-bold" style={{ color: colors.primary }}>₹{item.price}</span>
                    <span className="text-xs flex items-center gap-1" style={{ color: colors.textSecondary }}>
                      <FaClock className="text-xs" /> {item.scheduledTime || 'Select Time'}
                    </span>
                    <span className="text-xs flex items-center gap-1" style={{ color: colors.textSecondary }}>
                      {item.mealTypeSelected || 'Select Meal'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <button
                      className="w-7 h-7 rounded-full flex items-center justify-center transition-all"
                      style={{ 
                        backgroundColor: colors.primary + '20',
                        color: colors.primary
                      }}
                      onClick={() => dispatch(updateItemQuantity({ 
                        mealType: item.mealType, 
                        itemId: item.id, 
                        quantity: Math.max(0, item.quantity - 1) 
                      }))}
                    >
                      <FaMinus className="text-xs" />
                    </button>
                    <span className="font-bold w-6 text-center" style={{ color: colors.text }}>{item.quantity}</span>
                    <button
                      className="w-7 h-7 rounded-full flex items-center justify-center transition-all"
                      style={{ 
                        backgroundColor: colors.primary + '20',
                        color: colors.primary
                      }}
                      onClick={() => dispatch(updateItemQuantity({ 
                        mealType: item.mealType, 
                        itemId: item.id, 
                        quantity: item.quantity + 1 
                      }))}
                    >
                      <FaPlus className="text-xs" />
                    </button>
                  </div>
                  <button
                    className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-105"
                    style={{ backgroundColor: '#C6282820', color: '#C62828' }}
                    onClick={() => dispatch(removeOrderItem({ mealType: item.mealType, itemId: item.id }))}
                  >
                    <FaTrash className="text-xs" />
                  </button>
                </div>
              </div>
            ))}

            {/* Add Date/Time for meal */}
            <button
              className="mt-2 text-sm font-medium hover:underline"
              style={{ color: colors.primary }}
              onClick={() => {
                setSelectedMealForDate(items[0]?.id);
                setShowDatePicker(true);
              }}
            >
              + Add Date & Time
            </button>
          </div>
        ))}

        {/* Add New Date Button */}
        <button
          className="w-full py-4 rounded-2xl border-2 border-dashed font-semibold transition-all duration-300 hover:scale-105"
          style={{ 
            borderColor: colors.primary,
            color: colors.primary,
            backgroundColor: colors.primary + '10'
          }}
          onClick={() => setShowCalendar(true)}
        >
          + Add New Date
        </button>
      </div>

      {/* Date/Time Picker Modal */}
      <AnimatePresence>
        {showDatePicker && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          >
            <motion.div
              className="rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              style={{ 
                backgroundColor: colors.card,
                border: `1px solid ${colors.border}`
              }}
            >
              <h3 className="text-xl font-bold mb-4" style={{ color: colors.text }}>
                Schedule Meal
              </h3>
              
              <div className="mb-4">
                <label className="text-sm font-medium block mb-1" style={{ color: colors.textSecondary }}>
                  Select Date
                </label>
                <div 
                  className="flex items-center justify-between p-3 rounded-xl cursor-pointer"
                  style={{ 
                    backgroundColor: colors.inputBackground,
                    border: `1px solid ${colors.border}`
                  }}
                  onClick={() => setShowCalendar(true)}
                >
                  <span style={{ color: colors.text }}>{formatDate(selectedDate)}</span>
                  <FaCalendarAlt style={{ color: colors.primary }} />
                </div>
              </div>

              <div className="mb-4">
                <label className="text-sm font-medium block mb-1" style={{ color: colors.textSecondary }}>
                  Select Meal Time
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {mealTypes.map((type) => (
                    <button
                      key={type}
                      className="py-2 px-3 rounded-xl text-sm font-medium transition-all hover:scale-105"
                      style={{ 
                        backgroundColor: selectedMealType === type ? colors.primary : colors.inputBackground,
                        color: selectedMealType === type ? '#FFFFFF' : colors.text,
                        border: `1px solid ${selectedMealType === type ? colors.primary : colors.border}`
                      }}
                      onClick={() => setSelectedMealType(type)}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <label className="text-sm font-medium block mb-1" style={{ color: colors.textSecondary }}>
                  Select Time Slot
                </label>
                <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                  {timeSlots.slice(0, 18).map((time) => (
                    <button
                      key={time}
                      className="py-2 px-2 rounded-xl text-xs font-medium transition-all hover:scale-105"
                      style={{ 
                        backgroundColor: selectedTimeSlot === time ? colors.primary : colors.inputBackground,
                        color: selectedTimeSlot === time ? '#FFFFFF' : colors.text,
                        border: `1px solid ${selectedTimeSlot === time ? colors.primary : colors.border}`
                      }}
                      onClick={() => setSelectedTimeSlot(time)}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 mt-4">
                <button
                  className="flex-1 py-3 rounded-xl font-semibold transition-all hover:scale-105"
                  style={{ backgroundColor: colors.primary, color: '#FFFFFF' }}
                  onClick={() => {
                    if (selectedDate && selectedMealForDate) {
                      handleAddDateForMeal(selectedMealForDate, selectedDate, selectedMealType, selectedTimeSlot);
                    }
                    setShowDatePicker(false);
                  }}
                >
                  Confirm Schedule
                </button>
                <button
                  className="flex-1 py-3 rounded-xl font-semibold transition-all hover:scale-105"
                  style={{ 
                    backgroundColor: colors.border,
                    color: colors.text
                  }}
                  onClick={() => setShowDatePicker(false)}
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center mt-6">
        <Link to="/meal-select" className="btn-secondary">
          ← Back
        </Link>
        <Link to="/time-select">
          <button className="btn-primary">
            Proceed to Time <FaArrowRight className="inline ml-2" />
          </button>
        </Link>
      </div>
    </motion.div>
  );
};

export default DateSelect;